import { useEffect, useRef, useState } from 'react';

export type TabIndicatorStyle = {
  left?: number;
  width?: number;
};

interface UseTabIndicatorOptions {
  /** Re-measure on window resize. Defaults to true. */
  trackResize?: boolean;
  /** Extra deps that change tab geometry (e.g. the tabs array). */
  deps?: React.DependencyList;
}

/**
 * Tracks the active tab's position so an absolutely-positioned indicator can
 * slide to it. Returns a ref to spread across the tab elements and the measured
 * `left`/`width` of the active one.
 */
export function useTabIndicator<T extends HTMLElement = HTMLElement>(
  activeIndex: number,
  options: UseTabIndicatorOptions = {},
) {
  const { trackResize = true, deps = [] } = options;

  const tabRef = useRef<(T | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState<TabIndicatorStyle>({});

  useEffect(() => {
    const updateIndicator = () => {
      const node = tabRef.current[activeIndex];
      if (node) {
        setIndicatorStyle({ left: node.offsetLeft, width: node.offsetWidth });
      }
    };

    updateIndicator();

    if (!trackResize) {
      return;
    }

    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [activeIndex, trackResize, ...deps]);

  return { tabRef, indicatorStyle };
}
